import {format} from 'date-fns';
import {combine, merge, sample} from 'effector';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {dateModel} from '@entities/date/model';
import {DateRange} from '@entities/date/types';
import {createGridTableModel, initFiltersTable, initSortingsTable} from '@shared/composables/table';
import {InnerFilters, InnerSortings} from '@shared/types/grid';
import {SelectOption} from '@shared/ui/Select';

import * as api from './api';
import {TaxpayerListResponse, TaxpayersRequest} from './types';

const domain = rootDomain.createDomain('Taxpayers');

const fetchTaxpayersFx = domain.createEffect(api.fetchTaxpayers);
fetchTaxpayersFx.failData.watch(showRestError);

export const downloadTaxpayersExcelFx = domain.createEffect(api.downloadTaxpayersExcel);
downloadTaxpayersExcelFx.failData.watch(showRestError);

const grid = createGridTableModel({domain});

export const {
    filters$,
    filtersChanged,
    filtersReset,
    defaultFilters,
    sortings$,
    sortingsChanged,
    sortingsReset,
    defaultSortings,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    Gate: TaxpayersGate,
    tableStateChanged,
    stateReset,
} = grid;

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

const data$ = domain
    .createStore<TaxpayerListResponse | null>(null)
    .on(fetchTaxpayersFx.doneData, (_, data) => data)
    .reset(TaxpayersGate.close);

const searchChanged = domain.createEvent<string>();
const search$ = domain
    .createStore('')
    .on(searchChanged, (_, value) => value)
    .reset(stateReset);

const downloadExcel = domain.createEvent();

pageNumber$.reset(filtersChanged, sortingsChanged, searchChanged, dateModel.changed, stateReset);
pageSize$.reset(stateReset);

const getSelectValue = <T>(value: unknown): T | undefined => {
    if (!value) return undefined;
    if (typeof value === 'object' && 'value' in (value as SelectOption<T>)) {
        return (value as SelectOption<T>).value ?? undefined;
    }
    return value as T;
};

const getRangeParams = (filters: InnerFilters, id: string, key: string) => {
    const range = filters[id];
    if (!Array.isArray(range)) return {};

    const [from, to] = range;
    return {
        ...(from != null && from !== '' && {[`from${key}`]: String(from)}),
        ...(to != null && to !== '' && {[`to${key}`]: String(to)}),
    };
};

const getFilterParam = <T>(filters: InnerFilters, id: string) => {
    const value = getSelectValue<T>(filters[id]);
    return value === undefined ? {} : {[id]: value};
};

const toRequest = ({
    pageNumber,
    pageSize,
    order,
    filters,
    search,
    date,
}: {
    pageNumber: number;
    pageSize: number;
    order: InnerSortings;
    filters: InnerFilters;
    search: string;
    date: {range: DateRange};
}): TaxpayersRequest => {
    const sorting = Object.entries(order)[0];
    const periodDate = date.range.from ?? new Date();

    return {
        period: format(periodDate, 'yyyy-MM'),
        page: pageNumber + 1,
        limit: pageSize,
        ...(sorting?.[0] && sorting[1] !== undefined
            ? {sortBy: sorting[0], direction: sorting[1] ? 'ASC' : 'DESC'}
            : {}),
        ...(search && {filter: search}),
        ...getFilterParam<boolean>(filters, 'liquidated'),
        ...getFilterParam(filters, 'surColour'),
        ...getFilterParam(filters, 'transitOrGap'),
        ...getFilterParam<boolean>(filters, 'isDecFiled'),
        ...getFilterParam(filters, 'decType'),
        ...getFilterParam(filters, 'gapType'),
        ...getRangeParams(filters, 'gapSum', 'GapSum'),
        ...getRangeParams(filters, 'shareDeduction', 'ShareDeduction'),
        ...getRangeParams(filters, 'sumPayNdsDeclaration', 'SumPayNdsDeclaration'),
        ...getRangeParams(filters, 'sumNdsDeductionDeclaration', 'SumNdsDeductionDeclaration'),
        ...getRangeParams(filters, 'saldoDeclaration', 'SaldoDeclaration'),
        ...getRangeParams(filters, 'sumPayNdsInvoice', 'SumPayNdsInvoice'),
        ...getRangeParams(filters, 'sumNdsDeductionInvoice', 'SumNdsDeductionInvoice'),
        ...getRangeParams(filters, 'saldoInvoice', 'SaldoInvoice'),
    };
};

const request$ = combine({
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    order: sortings$,
    filters: filters$,
    search: search$,
    date: dateModel.state,
});

sample({
    clock: merge([TaxpayersGate.open, tableStateChanged, searchChanged, dateModel.changed]),
    source: request$,
    filter: TaxpayersGate.status,
    fn: toRequest,
    target: fetchTaxpayersFx,
});

sample({
    clock: downloadExcel,
    source: request$,
    fn: toRequest,
    target: downloadTaxpayersExcelFx,
});

const $taxpayersState = combine({
    data: data$,
    pageCount: data$.map((data) => data?.totalPages ?? 0),
    totalItems: data$.map((data) => data?.totalItems ?? 0),
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    sortings: sortings$,
    search: search$,
    pending: fetchTaxpayersFx.pending,
    exportPending: downloadTaxpayersExcelFx.pending,
});

export const taxpayersModel = {
    state: $taxpayersState,
    filtersChanged,
    filtersReset,
    sortingsChanged,
    sortingsReset,
    pageNumberChanged,
    pageSizeChanged,
    searchChanged,
    downloadExcel,
};
